


































import {
  defineComponent,
  ref,
  provide,
  computed,
  watch,
  toRef,
  onBeforeUnmount,
  onBeforeMount,
  set,
} from "@vue/composition-api";
import { Route } from "vue-router";
import {
  ContentFile,
  InputContentFileCreate,
  InputContentFileUpdate,
} from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { MetadataField } from "@scrile/api-provider/dist/api/FileProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import logger from "@/modules/logger";
import { AttachmentFile, PostFileData } from "@/types";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";
import { AddingFilesOptions, DraggableEmitOptions } from "../../types";
import ChoosePostTypeController from "../ChoosePostTypeController";
import CreatePostView from "../CreatePostView";
import GoingLiveConfirm from "../GoingLiveConfirm";

interface FileUploadData {
  id: string;
  file: File;
  uploadProgress: number;
}

function isFileUploadData(data: any): data is FileUploadData {
  return typeof data.file !== "undefined";
}

export default defineComponent({
  name: "CreatePostController",
  components: { CreatePostView, ModalView, ChoosePostTypeController, GoingLiveConfirm },
  inject: [],
  props: {
    postId: { type: String, required: true },
    type: { type: String as () => ContentFeedTypeEnum, required: true },
    show: { type: Boolean, required: true },
    userId: { type: String, required: true },
  },
  setup(props, { root, emit }) {
    const loading = ref(false);
    const loadingUploadUrls = ref(false);
    const saving = ref<Promise<any> | null>(null);
    const postData = ref<ContentSet | null>(null);
    const postFiles = ref<(ContentFile | FileUploadData)[]>([]);
    const updateCb = ref<(() => Promise<void>)[]>([]);
    const showLiveConfirm = ref(false);
    const postType = ref<ContentFeedTypeEnum>();

    const showChooseType = computed(() => !loading.value && !postData.value && !showLiveConfirm.value);

    const isPostEmpty = computed(() => {
      if (!postData.value) return true;
      return !(postData.value?.description || postData.value?.title || postFiles.value.length > 0);
    });

    const postFileDataList = computed<PostFileData[]>(() => {
      return postFiles.value.map(i => {
        if (isFileUploadData(i)) {
          return {
            url: "",
            fileName: i.file.name,
            duration: null,
            uploading: true,
            uploadProgress: i.uploadProgress,
            mediaType: getFileMimeType(i.file),
            size: i.file.size,
            file: i.file,
            public: false,
            id: i.id,
            processingStatus: "NEW",
            metadata: {},
          };
        } else {
          return {
            id: i.id,
            mediaType: i.mediaType,
            fileName: i.title,
            size: i.size,
            public: i.publicAccess,
            uploading: false,
            duration: i.duration,
            url: i.urlPart,
            uploadProgress: 100,
            processingStatus: i.processingStatus,
            metadata: "metadata" in i ? i["metadata"] : {},
          };
        }
      });
    });

    const requestAndSetPostData = async (postId: string) => {
      return providers.ContentSetProvider.search({
        data: {
          userId: props.userId,
          ids: [postId],
        },
      }).then(resp => {
        postData.value = resp.result.find(i => i.id === postId) || null;
        emit("updatePostData", postData.value);
      });
    };

    const requestAndSetPostFiles = async (postId: string) => {
      return providers.ContentFilesProvider.search({
        data: {
          setId: [postId],
          size: 9999,
          sort: "SORT,ASC",
        },
      }).then(resp => {
        postFiles.value = resp.result;
      });
    };

    const watchPostId = async (postId: string) => {
      if (!postData.value && postId) {
        try {
          loading.value = true;
          await Promise.all([requestAndSetPostData(postId), requestAndSetPostFiles(postId)]);
        } finally {
          loading.value = false;
        }
      }
    };

    watch(toRef(props, "postId"), (postId: string) => watchPostId(postId), { immediate: true });

    const createPost = async (type: ContentFeedTypeEnum) => {
      try {
        loading.value = true;
        postData.value = await providers.ContentSetProvider.create({
          data: {
            type,
            userId: props.userId,
          },
        });
        emit("updatePostData", postData.value);
      } finally {
        loading.value = false;
      }
    };

    const onCreatePost = async (type: ContentFeedTypeEnum, cb?: () => void) => {
      try {
        await createPost(type);
      } finally {
        cb && cb();
      }
    };

    const onSetPostType = (type: ContentFeedTypeEnum) => {
      postType.value = type;
    };

    const onSetPostData = (data: ContentSet) => {
      postData.value = data;
      requestAndSetPostFiles(data.id);
    };

    const onSetUpdateCb = (cb: () => Promise<void>) => {
      updateCb.value.push(cb);
    };

    const getPostData = async () => {
      return postData.value || (await new Promise<ContentSet>(r => emit("updatePostData", (p: any) => r(p))));
    };

    const onFileRemove = async (fileId: string) => {
      const file = postFiles.value.find(f => f.id === fileId);
      if (!file) return;

      postFiles.value = postFiles.value.filter(i => i.id !== fileId);
      if (isFileUploadData(file)) {
        FileUploader.removeFromQueue(fileId);
      } else {
        const post = await getPostData();
        await providers.ContentFilesProvider.delete({ setId: post.id, id: fileId });
      }
    };

    const reset = () => {
      emit("close");
      if (postData.value && props.type !== ContentFeedTypeEnum.STORY) {
        emitter.emit(EVENTS.CONTENT_FEED_UPDATED, { userId: postData.value.userId, feedId: postData.value.id });
      }
      postData.value = null;
      postFiles.value.forEach(i => {
        if (isFileUploadData(i)) {
          onFileRemove(i.id);
        }
      });
      postFiles.value.length > 0 && (postFiles.value = []);
    };

    const setPostFileSorting = async (postId: string) => {
      if (!postFiles.value.find(i => isFileUploadData(i))) {
        for (const [index, fileData] of postFiles.value.entries()) {
          await providers.ContentFilesProvider.update({
            setId: postId,
            data: {
              id: fileData.id,
              sort: index,
            },
          });
        }
      }
    };

    const waitFileUpload = async (): Promise<void> => {
      if (postFileDataList.value.find(i => i.uploading)) {
        await new Promise(r => setTimeout(r, 500));
        return waitFileUpload();
      }
    };

    const setPostFilePublicAccess = async (postId: string) => {
      const filesUpdatePromises = postFiles.value.map(
        file =>
          (!isFileUploadData(file) &&
            providers.ContentFilesProvider.update({
              setId: postId,
              data: {
                id: file.id,
                publicAccess: file.publicAccess,
                metadata: file.metadata,
              },
            })) ||
          undefined
      );
      await Promise.all<void | ContentFile>(filesUpdatePromises);
    };

    const onUpdate = async (
      {
        data,
        closeOnSave,
        beforeUpdate,
      }: {
        data: Omit<InputContentSetUpdate, "id">;
        closeOnSave?: boolean;
        beforeUpdate?: () => Promise<void[]>;
      },
      cb?: Function
    ) => {
      const post = await getPostData();
      let deferred: Function = () => {};
      try {
        saving.value = new Promise<any>(r => {
          deferred = r;
        });
        const promises: Promise<any>[] = [setPostFilePublicAccess(post.id)];
        if (data.published) {
          promises.push(waitFileUpload(), setPostFileSorting(post.id));
        }
        await Promise.all(promises);
        beforeUpdate && (await beforeUpdate());
        postData.value = await providers.ContentSetProvider.update({
          data: {
            ...data,
            id: post.id,
          },
        });
        emit("updatePostData", postData.value);
        if (closeOnSave) {
          reset();
        }
      } finally {
        cb && cb();
        deferred();
        saving.value = null;
      }
    };

    const updatePostData = (value: ContentSet) => {
      postData.value = value;
    };

    const onFileUploaded = async (
      {
        id,
        uuid,
        file,
        post,
        publicAccess,
        metadata,
      }: {
        id: string;
        uuid: string;
        file: File;
        post: ContentSet;
        publicAccess: boolean;
        metadata: Record<MetadataField | string, string>;
      },
      afterFileUploadCB?: (file?: ContentFile) => void
    ) => {
      const inputFileData: InputContentFileCreate = {
        title: file.name,
        name: file.name,
        uuid,
        publicAccess,
        metadata,
      };
      const contentFile = await providers.ContentFilesProvider.create({ setId: post.id, data: inputFileData });
      afterFileUploadCB && afterFileUploadCB(contentFile);
      postFiles.value = postFiles.value.map(i => {
        return i.id === id ? contentFile : i;
      });
      setPostFileSorting(post.id);
    };

    const onAddFiles = async (
      files: FileList | File[],
      {
        unshift = false,
        publicAccess = false,
        metadata = {},
      }: { unshift?: boolean; publicAccess?: boolean; metadata?: Record<MetadataField | string, string> } = {},
      afterFileUploadCB?: (file?: ContentFile) => void
    ) => {
      try {
        loadingUploadUrls.value = true;
        if (postData.value?.type === ContentFeedTypeEnum.STORY && postFiles.value.length) {
          await onFileRemove(postFiles.value[0].id);
        }
        const post = await getPostData();
        const uploadUrls = await providers.ContentFilesProvider.getUploadUrls({
          setId: post.id,
          count: files.length,
        });
        const uploadData: UploadFileData[] = [];
        Array.from(files).forEach((file, index) => {
          const uuid = uploadUrls[index].uuid;
          const id = "file__" + uuid;
          postFiles.value[unshift ? "unshift" : "push"]({ id, file, uploadProgress: 0 });
          const data: UploadFileData = {
            urlPart: uploadUrls[index].urlPart,
            fileType: getFileMimeType(file),
            file,
            id,
            onSuccess: () => onFileUploaded({ id, post, file, uuid, publicAccess, metadata }, afterFileUploadCB),
            onUploadProgress: p => {
              const index = postFiles.value.findIndex(i => i.id === id && isFileUploadData(i));
              if (index !== -1) {
                set(postFiles.value[index], "uploadProgress", calculateProgress(p.total ?? 0, p?.loaded ?? 0));
              }
            },
            onError: e => {
              if (!postFiles.value.some(i => i.id === id)) {
                return;
              }
              logger.debug("Upload error", { dataId: data.id, dataPartUrl: data.urlPart, error: e });
              console.error(e);
              showDefaultUploadError();
              onFileRemove(data.id);
            },
          };
          uploadData.push(data);
        });
        FileUploader.addToQueue(uploadData);
      } finally {
        loadingUploadUrls.value = false;
      }
    };

    const onFileChange = async (data: InputContentFileUpdate) => {
      postFiles.value = postFiles.value.map(i => {
        if (i.id === data.id && !isFileUploadData(i)) {
          return { ...i, ...data };
        }
        return i;
      });
    };

    const onDraggableUpdate = async ({ newDraggableIndex, oldDraggableIndex }: DraggableEmitOptions) => {
      postFiles.value.splice(newDraggableIndex, 0, postFiles.value.splice(oldDraggableIndex, 1)[0]);
      try {
        await setPostFileSorting(postData.value?.id ?? "");
      } catch (e) {
        postFiles.value.splice(oldDraggableIndex, 0, postFiles.value.splice(newDraggableIndex, 1)[0]);
      }
    };

    const saveDraft = async () => {
      const update = () => Promise.all(updateCb.value.map(i => i()));
      return onUpdate({ data: { published: false }, closeOnSave: false, beforeUpdate: update });
    };

    const deleteDraft = async () => {
      await saving.value;
      if (!postData.value) return;
      await providers.ContentSetProvider.delete({ id: postData.value.id });
    };

    const onSaveStoryDraft = async () => {
      if (postData.value?.type === ContentFeedTypeEnum.STORY && postFileDataList.value.length !== 0) {
        await saving.value;
        await saveDraft();
        emit("close");
      }
    };

    const onCloseModal = async () => {
      if (postData.value?.type === ContentFeedTypeEnum.STORY && postFileDataList.value.length !== 0) {
        postFiles.value = [];
        return;
      }
      await saving.value;
      if (postData.value && !postData.value.published) {
        isPostEmpty.value ? await deleteDraft() : await saveDraft();
        emit("close");
        return;
      }
      reset();
    };

    const onAddFilesFromVault = async (files: AttachmentFile[], options?: AddingFilesOptions) => {
      if (postData.value?.type === ContentFeedTypeEnum.STORY && postFiles.value.length) {
        await onFileRemove(postFiles.value[0].id);
      }
      const post = await getPostData();
      for (const file of files) {
        const resultFile = await providers.ContentFilesProvider.create({
          setId: post.id,
          data: {
            name: file.metadata?.filename ?? file.uid,
            uuid: file.uid,
            publicAccess: options?.publicAccess ?? false,
            title: file.metadata?.filename ?? file.uid,
            ...(options?.metadata?.stereoFormat ? { metadata: { stereoFormat: options.metadata.stereoFormat } } : {}),
          },
        });
        postFiles.value.push(resultFile);
      }
    };

    onBeforeMount(() => {
      if (props.type.length > 0) onSetPostType(props.type);
    });

    const unWatch = watch(toRef(root, "$route"), (to: Route, from: Route | null) => {
      if (!from || to.name === from.name) return;
      onCloseModal();
    });

    const onPriceChanged = ({
      recurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      if (postData.value?.type === ContentFeedTypeEnum.RICH_TEXT) return;
      const publicAccess = !recurringLevel && !oneTimePackage;
      if (
        (publicAccess && postFileDataList.value.some(f => !f.public)) ||
        (!publicAccess && postFileDataList.value.every(f => f.public))
      ) {
        postFiles.value = postFiles.value.map(file => {
          if (!isFileUploadData(file)) {
            file.publicAccess = publicAccess;
          }
          return file;
        });
      }
    };
    emitter.on<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
      unWatch();
    });

    provide("contentFeedUserId", props.userId);
    provide("fileList", postFileDataList);
    provide("updatePostData", updatePostData);
    provide("postAddFiles", onAddFiles);
    provide("postAddFilesFromVault", onAddFilesFromVault);
    provide("loadingUploadUrls", loadingUploadUrls);

    return {
      loading,
      showChooseType,
      showLiveConfirm,
      postType,
      postData,
      postFileDataList,
      onFileRemove,
      onSetUpdateCb,
      onAddFiles,
      onUpdate,
      onSetPostType,
      onSetPostData,
      onCreatePost,
      onCloseModal,
      onAddFilesFromVault,
      onFileChange,
      onDraggableUpdate,
      onSaveStoryDraft,
    };
  },
});
